import { useState, useEffect } from 'react';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { useQuery } from '@tanstack/react-query-v4';
import { Flex, Autocomplete, TextField, Icon, Spinner } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { CommunicationService } from 'react-scale/corp/views/CorpProjectGroupManager/communication/communicationService';
import { useGetUserEmailsByIds } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { isEqual } from 'lodash';

interface UserAutoCompleteProps {
  userIds: string[];
  onUserIdsChange: (userIds: string[]) => void;
  limitToScaliens: boolean;
  limitToContributors: boolean;
  showValues?: boolean;
  nonRemovableUsers?: string[];
}

export const UserAutocomplete = ({
  userIds,
  onUserIdsChange,
  limitToScaliens,
  limitToContributors,
  showValues = true,
  nonRemovableUsers = [],
}: UserAutoCompleteProps) => {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebouncedValue(searchInput, 500);
  const [fetchedUserEmails, setFetchedUserEmails] = useState<Record<string, string>>({});

  // Fetches the users to show up as options based on the search input
  const { data: userData, isFetching: isFetchingUserData } = useQuery({
    queryKey: ['user-autocomplete', limitToScaliens, debouncedSearchInput],
    queryFn: ({ signal }) => {
      if (limitToScaliens) {
        return CommunicationService.lookupScaliens(debouncedSearchInput, { signal });
      }

      return CommunicationService.lookupUsersByEmail(debouncedSearchInput, {
        signal,
        limitToContributors,
      });
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const userOptions = userData?.users.map(user => ({
    value: user.id,
    label: user.email,
  }));
  const fetchedUserOptions = Object.entries(fetchedUserEmails)
    .map(([id, email]) => ({
      value: id,
      label: email,
    }))
    .filter(option => !userOptions?.some(user => user.value === option.value));
  const allUserOptions = userOptions && userOptions.length > 0 ? userOptions : fetchedUserOptions;

  // Fetches the emails for the currently selected users
  const userIdsToFetch = userIds.filter(id => !fetchedUserEmails[id]);
  const { data: newUserEmails, isFetching: isFetchingUserEmails } = useGetUserEmailsByIds({
    userIds: userIdsToFetch,
  });

  useEffect(() => {
    if (newUserEmails && Object.keys(newUserEmails).length > 0) {
      setFetchedUserEmails(prev => ({
        ...prev,
        ...newUserEmails,
      }));
    }
  }, [newUserEmails]);

  // TODO: Once Radix is migrated to 1.36, we can use the AsyncItems and AsyncValues to replace the loading state
  return (
    <Autocomplete.Root
      multiple
      search={searchInput}
      onSearchChange={setSearchInput}
      value={userIds}
      onValueChange={(newUserIds: string[]) => {
        if (!isEqual(newUserIds, userIds)) {
          onUserIdsChange(newUserIds);
        }
      }}
    >
      <Autocomplete.Content>
        <Autocomplete.TextField
          placeholder="Search by email..."
          // Do not allow invalid values from paste
          onPaste={() => {}}
          style={{ pointerEvents: 'auto' }}
        >
          <TextField.Slot side="left">
            <Icon icon={faSearch} label="Search" />
          </TextField.Slot>
          <Autocomplete.Values />
          <Autocomplete.Reset />
        </Autocomplete.TextField>

        <Autocomplete.Items>
          {isFetchingUserData || isFetchingUserEmails
            ? [
                <Autocomplete.Item
                  key={'fetching-users'}
                  keywords={searchInput}
                  value={searchInput}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <Flex direction="row" gap="2">
                    <Spinner />
                    {'Fetching users...'}
                  </Flex>
                </Autocomplete.Item>,
              ]
            : allUserOptions?.map(user => (
                <Autocomplete.Item
                  key={user.value}
                  value={user.value}
                  disabled={nonRemovableUsers?.includes(user.value)}
                >
                  {showValues ? user.label : user.value}
                </Autocomplete.Item>
              ))}
        </Autocomplete.Items>
      </Autocomplete.Content>
    </Autocomplete.Root>
  );
};
